import { clsx } from 'clsx';
import { useState, type ReactNode } from 'react';
import { StatusBadge } from './StatusBadge';
import { PriorityBadge } from './PriorityBadge';
import { TicketSourceBadge } from './TicketSourceBadge';
import { ExternalRecordLink, externalLinkLabel } from '@components/common/ExternalRecordLink';
import { PersonCell, type Person } from './PersonCell';
import type { SlaDerivation } from './ticketFacts';

/**
 * The ticket detail header, in two parts — mockup 4a, lines 76–105.
 *
 * `TicketCrumbBar` is the 52px row and the ONLY full-width band on the page.
 * `TicketIdentityPlate` is the bracketed plate, and it belongs *inside* the
 * centre column: that is what lets the record column run the full window height
 * beside the conversation instead of starting halfway down the page.
 */

type SlaState = 'ok' | 'due' | 'breached';

const STATUS_OPTIONS = ['new', 'in_progress', 'pending', 'resolved', 'closed'] as const;
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'] as const;

const CHANNEL_LABELS: Record<string, string> = {
  email: 'EMAIL',
  chat: 'CHAT',
  phone: 'VOICE',
  voice: 'VOICE',
  web: 'WEB',
  api: 'API',
  whatsapp: 'WHATSAPP',
  facebook: 'FACEBOOK',
  twitter: 'TWITTER',
};

/* ============================ crumb bar ============================ */

export function TicketCrumbBar({
  onBack,
  backLabel = 'Tickets',
  actions,
  listNav,
  meta,
  title,
}: {
  onBack: () => void;
  /** Where the back crumb goes. */
  backLabel?: string;
  /** Edit · Send survey · Resolve ticket — the header's second row. */
  actions?: ReactNode;
  /** Prev/next queue navigation. Absent on a deep link with no loaded list. */
  listNav?: ReactNode;
  /** The utility cluster, pinned to the top-right corner of row 1. */
  meta?: ReactNode;
  /** Ticket identity (number · origin · subject), under the back crumb. */
  title?: ReactNode;
}) {
  return (
    <header
      className="mb-[14px] mr-4 mt-4 flex min-w-0 flex-none flex-wrap items-start justify-between gap-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] px-[18px] py-3"
      style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}
    >
      {/* One row: back crumb + queue nav on the left; actions, then the
          utility cluster, on the right. */}
      <div className="min-w-0 flex-1">
      <div className="flex min-w-0 flex-wrap items-center gap-[14px]">
        <button
          type="button"
          onClick={onBack}
          className="flex flex-none items-center gap-[8px] font-mono text-[13px] font-bold uppercase leading-none tracking-[.13em] text-[var(--co-ink)] transition-colors duration-[120ms] hover:text-[var(--co-brass-ink)]"
        >
          <span aria-hidden="true" className="text-[15px]">←</span> {backLabel}
        </button>
        {listNav && (
          <>
            <span aria-hidden="true" className="h-4 w-px flex-none bg-[var(--co-line)]" />
            {listNav}
          </>
        )}
      </div>
      {title && <div className="mt-[12px]">{title}</div>}
      </div>

      <div className="flex flex-none flex-wrap items-center gap-[10px]">
        {actions}
        {actions && meta && (
          <span aria-hidden="true" className="mx-1 h-5 w-px flex-none bg-[var(--co-line)]" />
        )}
        {meta && <div className="flex h-[34px] flex-none items-center">{meta}</div>}
      </div>
    </header>
  );
}

/**
 * Prev/next within the queue the list page already loaded. Hidden — not
 * disabled — when there is no list, e.g. a deep link straight to a ticket.
 */
export function TicketListNav({
  index,
  total,
  onPrev,
  onNext,
}: {
  index: number;
  total: number;
  onPrev?: () => void;
  onNext?: () => void;
}) {
  const btn =
    'font-mono text-[10.5px] font-medium uppercase leading-none tracking-[.13em] text-[var(--co-ink-3)] transition-colors duration-[120ms] hover:text-[var(--co-brass-ink)] disabled:opacity-40 disabled:hover:text-[var(--co-ink-3)]';
  return (
    <span className="flex flex-none items-center gap-[14px]">
      <button type="button" onClick={onPrev} disabled={!onPrev} className={btn}>
        <span aria-hidden="true">◂</span> Prev
      </button>
      <button type="button" onClick={onNext} disabled={!onNext} className={btn}>
        Next <span aria-hidden="true">▸</span>
      </button>
      <span className="font-mono text-[10px] uppercase tracking-[.1em] text-[var(--co-ink-4)]">
        {index} of {total}
      </span>
    </span>
  );
}

/* ============================ plate ============================ */

interface TicketIdentityPlateProps {
  ticket: {
    ticketNumber?: string | number;
    subject?: string;
    status?: any;
    priority?: any;
    channel?: any;
    createdAt?: string;
    updatedAt?: string;
    /** Ticket source (see TicketSourceBadge); shown as "Created via …". */
    origin?: string | null;
    originName?: string | null;
    originType?: string | null;
    createdByApiKeyName?: string | null;
    /** The ticket's page in the external system that sent it. */
    externalUrl?: string | null;
    externalUrlLabel?: string | null;
  };
  /** Ticket type name, shown beside the channel as `EMAIL · RENEWAL`. */
  typeLabel?: string;
  /** Who raised it. Omit the prop to drop the cell. */
  requester?: Person;
  /** Makes the customer cell a link (e.g. to the customer profile). */
  onOpenRequester?: () => void;
  /** Who owns it. Pass `null` to render the unassigned state. */
  assignee?: Person | null;
  onChangeStatus?: (status: string) => void;
  onChangePriority?: (priority: string) => void;
  /** Makes the ASSIGNED cell click-to-reassign; renders `menu` beneath it. */
  onReassign?: () => void;
  /** The assignee picker, rendered under the ASSIGNED cell when open. */
  assigneeMenu?: ReactNode;
  isUpdating?: boolean;
  resolutionDue?: { remaining: string; at: string; state: SlaState };
  /** Full SLA detail (first response + resolution). Replaces `resolutionDue`. */
  slaDetail?: SlaDerivation;
  opened?: { elapsed: string; at: string };
  /** The tab strip, rendered below the plate. */
  tabs?: ReactNode;
  sla?: ReactNode;
  className?: string;
  /** False when the crumb bar already shows number · origin · subject. */
  showIdentity?: boolean;
}

const CHANNEL_LABEL_OF = (channel?: string | null) =>
  channel ? CHANNEL_LABELS[channel] || String(channel).toUpperCase() : '';

/** The subject, then ticket number · channel · type, side by side (wraps when narrow). */
export function TicketTitle({
  ticketNumber,
  channel,
  typeLabel,
  subject,
}: {
  ticketNumber?: string | number | null;
  channel?: string | null;
  typeLabel?: string;
  subject?: string | null;
}) {
  const originLine = [CHANNEL_LABEL_OF(channel), typeLabel?.toUpperCase()]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-1">
      <h1 className="min-w-0 max-w-[56ch] break-words [overflow-wrap:anywhere] font-display text-[26px] font-extrabold tracking-[-0.03em] leading-[1.22] text-[var(--co-ink)] [text-wrap:pretty]">
        {subject || 'Untitled ticket'}
      </h1>
      <div className="flex flex-none items-center gap-3">
        {ticketNumber != null && (
          <span className="flex-none font-mono text-[12px] font-semibold tracking-[.1em] text-[var(--co-brass-ink)]">
            #{ticketNumber}
          </span>
        )}
        {originLine && (
          <>
            <span aria-hidden="true" className="h-px w-[14px] flex-none bg-[var(--co-line)]" />
            <span className="truncate font-mono text-[10.5px] font-medium uppercase leading-none tracking-[.14em] text-[var(--co-ink-3)]">
              {originLine}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/** One metadata cell: mono label above a value, on the plan's 26px rhythm. */
function Cell({
  label,
  children,
  first,
  last,
}: {
  label: ReactNode;
  children: ReactNode;
  first?: boolean;
  last?: boolean;
}) {
  return (
    <div className={clsx('min-w-0', first ? 'pr-[26px]' : last ? 'xl:pl-[26px]' : 'pr-[26px] xl:pl-[26px]')}>
      <div className="font-mono text-[9.5px] font-medium uppercase leading-none tracking-[.16em] text-[var(--co-ink-4)]">
        {label}
      </div>
      <div className="mt-[7px] min-w-0">{children}</div>
    </div>
  );
}

// Hidden below xl, where the row wraps and a rule would dangle at a line end.
const Divider = () => (
  <span aria-hidden="true" className="hidden h-[38px] w-px flex-none self-center bg-[var(--co-line-subtle)] xl:block" />
);

export function TicketIdentityPlate({
  ticket,
  typeLabel,
  requester,
  onOpenRequester,
  assignee,
  onChangeStatus,
  onChangePriority,
  onReassign,
  assigneeMenu,
  isUpdating = false,
  resolutionDue,
  slaDetail,
  opened,
  tabs,
  sla,
  className,
  showIdentity = true,
}: TicketIdentityPlateProps) {
  const [openMenu, setOpenMenu] = useState<'status' | 'priority' | null>(null);

  /**
   * A badge that is also a control. Status and priority are fixed enums, so the
   * band can own these outright — no loaded data, no second control elsewhere.
   */
  const badgeMenu = (
    kind: 'status' | 'priority',
    badge: ReactNode,
    options: readonly string[],
    current: string,
    onPick: (value: string) => void,
    label: string,
  ) => (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpenMenu(openMenu === kind ? null : kind)}
        disabled={isUpdating}
        aria-expanded={openMenu === kind}
        title={label}
        className="inline-flex items-center gap-1.5 transition-opacity duration-[120ms] hover:opacity-80 disabled:opacity-50"
      >
        {badge}
        <span aria-hidden="true" className="text-[10px] text-[var(--co-ink-4)]">▾</span>
      </button>
      {openMenu === kind && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpenMenu(null)} />
          <div className="absolute right-0 z-30 mt-1.5 min-w-[150px] rounded-[2px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] py-1 shadow-[var(--co-shadow-pop)]">
            {options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setOpenMenu(null);
                  if (option !== current) onPick(option);
                }}
                className={clsx(
                  'block w-full px-3 py-[6px] text-left font-mono text-[10px] uppercase tracking-[.12em] transition-colors duration-[120ms]',
                  option === current
                    ? 'bg-[var(--co-brass-wash)] text-[var(--co-brass-ink)]'
                    : 'text-[var(--co-ink-3)] hover:bg-[var(--co-brass-wash)]',
                )}
              >
                {option.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  // Colour never carries the SLA alone — the RESOLUTION DUE label names it.
  const dueColor: Record<SlaState, string> = {
    ok: 'text-[var(--co-ink)]',
    due: 'text-[var(--co-terracotta)]',
    breached: 'text-[var(--co-burgundy)]',
  };

  const hasMeta =
    Boolean(requester) || assignee !== undefined || Boolean(resolutionDue) || Boolean(opened);
  const hasSlaDetail = Boolean(slaDetail?.firstResponse || slaDetail?.resolution);
  const slaBreached =
    slaDetail?.firstResponse?.state === 'breached' || slaDetail?.resolution?.state === 'breached';
  // SLA detail takes the Resolution-due slot when present.
  const showResolutionDue = Boolean(resolutionDue) && !hasSlaDetail;

  // Channel and type read as one mono string — `EMAIL · RENEWAL`.
  const channelLabel = CHANNEL_LABEL_OF(ticket.channel);
  const originLine = [channelLabel, typeLabel?.toUpperCase()].filter(Boolean).join(' · ');

  return (
    <div className={clsx('flex-none', className)}>
      {/* The plate is a floating card of its own, matching the crumb bar. */}
      <div
        className="relative border border-[var(--co-line)] bg-[var(--co-raised)] pb-[20px] pl-[24px] pr-[24px] pt-[22px]"
        style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}
      >
        {/* Without identity (crumb bar shows it), metadata and the badges share
            one row: metadata left, badges right. */}
        <div className={clsx(!showIdentity && 'flex flex-wrap items-center gap-x-6 gap-y-3')}>
        {/* Row 1 — identifiers left, state right. */}
        <div className={clsx('flex items-center gap-3 pr-[2px]', !showIdentity && 'order-last ml-auto')}>
          {showIdentity && ticket.ticketNumber != null && (
            <span className="flex-none font-mono text-[12px] font-semibold tracking-[.1em] text-[var(--co-brass-ink)]">
              #{ticket.ticketNumber}
            </span>
          )}

          {showIdentity && originLine && (
            <>
              <span aria-hidden="true" className="h-px w-[14px] flex-none bg-[var(--co-line)]" />
              <span className="truncate font-mono text-[10.5px] font-medium uppercase leading-none tracking-[.14em] text-[var(--co-ink-3)]">
                {originLine}
              </span>
            </>
          )}

          <div className="flex-1" />

          {/* The badges ARE the controls — the record column carries no second
              pair. One control per fact (phase 9 audit). */}
          <div className="flex items-center gap-3 pr-[6px]">
            <ExternalRecordLink url={ticket.externalUrl} label={externalLinkLabel(ticket)} />
            <TicketSourceBadge
              ticket={ticket}
              prefix="Created via"
              suffix={ticket.createdByApiKeyName ? ` · key ${ticket.createdByApiKeyName}` : undefined}
              className="max-w-[320px]"
            />
            {ticket.status &&
              (onChangeStatus
                ? badgeMenu(
                    'status',
                    <StatusBadge status={ticket.status} />,
                    STATUS_OPTIONS,
                    ticket.status,
                    onChangeStatus,
                    'Change status',
                  )
                : <StatusBadge status={ticket.status} />)}
            {ticket.priority &&
              (onChangePriority
                ? badgeMenu(
                    'priority',
                    <PriorityBadge priority={ticket.priority} showLabel variant="chip" />,
                    PRIORITY_OPTIONS,
                    ticket.priority,
                    onChangePriority,
                    'Change priority',
                  )
                : <PriorityBadge priority={ticket.priority} showLabel variant="chip" />)}
          </div>
        </div>

        {/* Row 2 — the subject */}
        {showIdentity && (
          <h1 className="mt-[14px] max-w-[56ch] font-display text-[29px] font-extrabold tracking-[-0.03em] leading-[1.22] text-[var(--co-ink)] [text-wrap:pretty]">
            {ticket.subject || 'Untitled ticket'}
          </h1>
        )}

        {/* Row 3 — identity metadata */}
        {hasMeta && (
          <div
            className={clsx(
              'flex flex-wrap items-stretch gap-y-3',
              showIdentity
                ? 'mt-[18px] border-t border-[var(--co-line-subtle)] pt-[16px]'
                : 'min-w-0',
            )}
          >
            {requester && (
              <Cell label="Customer" first>
                {onOpenRequester ? (
                  <button
                    type="button"
                    onClick={onOpenRequester}
                    title="View customer profile"
                    className="group -m-1.5 flex min-w-0 items-center rounded-[10px] p-1.5 text-left transition-colors duration-[120ms] hover:bg-[var(--co-canvas)]"
                  >
                    <PersonCell
                      person={requester}
                      nameClassName="text-[var(--co-brass-ink)] group-hover:underline"
                      trailing={
                        <span
                          aria-hidden="true"
                          className="ml-1 flex-none text-[15px] leading-none text-[var(--co-ink-4)] transition-transform duration-[120ms] group-hover:translate-x-0.5 group-hover:text-[var(--co-brass-ink)]"
                        >
                          ›
                        </span>
                      }
                    />
                  </button>
                ) : (
                  <PersonCell person={requester} />
                )}
              </Cell>
            )}

            {requester && assignee !== undefined && <Divider />}

            {assignee !== undefined && (
              <Cell label="Assigned">
                <div className="relative">
                  {onReassign ? (
                    <button
                      type="button"
                      onClick={onReassign}
                      className="group flex min-w-0 items-center gap-2 text-left"
                      title="Reassign"
                    >
                      {assignee ? (
                        <PersonCell person={assignee} />
                      ) : (
                        <span className="font-mono text-[12px] uppercase tracking-[.1em] text-[var(--co-terracotta)]">
                          Unassigned
                        </span>
                      )}
                      <span
                        aria-hidden="true"
                        className="flex-none text-[10px] text-[var(--co-ink-4)] transition-colors duration-[120ms] group-hover:text-[var(--co-brass-ink)]"
                      >
                        ▾
                      </span>
                    </button>
                  ) : assignee ? (
                    <PersonCell person={assignee} />
                  ) : (
                    <span className="font-mono text-[13px] text-[var(--co-line-strong)]">—</span>
                  )}
                  {assigneeMenu}
                </div>
              </Cell>
            )}

            {(assignee !== undefined || requester) && (showResolutionDue || hasSlaDetail) && <Divider />}

            {hasSlaDetail && (
              <Cell
                label={
                  slaBreached ? (
                    // Breached: the label itself becomes the red chip.
                    <span className="inline-flex items-center gap-1 rounded-full border border-[var(--co-burgundy)] px-2 py-0.5 font-sans text-[11px] font-semibold normal-case tracking-normal text-[var(--co-burgundy)]">
                      <span aria-hidden="true">▲</span> SLA breached
                    </span>
                  ) : (
                    'SLA'
                  )
                }
              >
                <div className="min-w-[210px] space-y-1 text-[13px]">
                  {([
                    ['First response', slaDetail!.firstResponse],
                    ['Resolution', slaDetail!.resolution],
                  ] as const).map(([label, policy]) =>
                    policy ? (
                      <div key={label} className="flex items-baseline justify-between gap-4">
                        <span className="text-[var(--co-ink-3)]">{label}</span>
                        <span
                          className={clsx(
                            'font-medium',
                            policy.state === 'met'
                              ? 'text-[var(--co-green)]'
                              : policy.state === 'breached'
                                ? 'text-[var(--co-burgundy)]'
                                : policy.state === 'due'
                                  ? 'text-[var(--co-terracotta)]'
                                  : policy.state === 'paused'
                                    ? 'text-[var(--co-ink-3)]'
                                    : 'text-[var(--co-ink)]',
                          )}
                          title={
                            policy.state === 'paused'
                              ? 'SLA paused — the clock resumes and the deadline moves out when the ticket leaves this status'
                              : undefined
                          }
                        >
                          {policy.state === 'met'
                            ? label === 'Resolution' ? 'Resolved' : 'Responded'
                            : policy.state === 'paused'
                              ? 'Paused'
                              : policy.at}
                        </span>
                      </div>
                    ) : null,
                  )}
                </div>
              </Cell>
            )}

            {showResolutionDue && resolutionDue && (
              <Cell label="Resolution due">
                <span
                  className={clsx(
                    'block font-mono text-[15px] font-semibold uppercase leading-none',
                    dueColor[resolutionDue.state],
                  )}
                >
                  {resolutionDue.state === 'breached' && <span aria-hidden="true">▲ </span>}
                  {resolutionDue.remaining}
                </span>
                {resolutionDue.at && (
                  <span className="mt-[5px] block font-mono text-[11.5px] leading-none text-[var(--co-ink-4)]">
                    {resolutionDue.at}
                  </span>
                )}
              </Cell>
            )}

            {(showResolutionDue || hasSlaDetail) && opened && <Divider />}

            {opened && (
              <Cell label="Opened" last>
                <span className="block font-mono text-[13px] font-medium leading-none text-[var(--co-ink-2)]">
                  {opened.elapsed}
                </span>
                {opened.at && (
                  <span className="mt-[5px] block font-mono text-[11.5px] leading-none text-[var(--co-ink-4)]">
                    {opened.at}
                  </span>
                )}
              </Cell>
            )}
          </div>
        )}

        </div>

        {sla && <div className="mt-[14px]">{sla}</div>}
      </div>

      {/* Tab strip sits below the plate. */}
      {tabs && <div className="mt-[14px]">{tabs}</div>}
    </div>
  );
}

/**
 * @deprecated Phase 10 split this into `TicketCrumbBar` (full width) and
 * `TicketIdentityPlate` (inside the centre column). Rendering both together
 * puts the plate over the record column, which is the bug the split fixes.
 * Kept so older call sites still compile.
 */
export function TicketDetailHeader({
  onBack,
  actions,
  ...plate
}: { onBack: () => void; actions?: ReactNode } & TicketIdentityPlateProps) {
  return (
    <>
      <TicketCrumbBar onBack={onBack} actions={actions} />
      <TicketIdentityPlate {...plate} />
    </>
  );
}

/**
 * Two SLA bars — first response and resolution. 3px tall, full width, no card.
 */
export function SlaBars({
  firstResponse,
  resolution,
}: {
  firstResponse?: { label: string; pct: number; state: 'met' | 'due' | 'breached' };
  resolution?: { label: string; pct: number; state: 'met' | 'due' | 'breached' };
}) {
  const tone = {
    met: { text: 'text-[var(--co-green)]', bar: 'bg-[var(--co-green-soft)]', glyph: '✓' },
    due: { text: 'text-[var(--co-terracotta)]', bar: 'bg-[var(--co-terracotta-soft)]', glyph: '◑' },
    breached: { text: 'text-[var(--co-burgundy)]', bar: 'bg-[var(--co-burgundy)]', glyph: '▲' },
  };

  const row = (
    title: string,
    data: { label: string; pct: number; state: 'met' | 'due' | 'breached' },
  ) => {
    const t = tone[data.state];
    return (
      <div className="min-w-0 flex-1">
        <div className="mb-[6px] flex items-baseline justify-between gap-3">
          <span className="text-[12.5px] text-[var(--co-ink-3)]">{title}</span>
          <span
            className={clsx(
              'font-mono text-[10.5px] font-medium uppercase leading-none tracking-[.1em]',
              t.text,
            )}
          >
            <span aria-hidden="true">{t.glyph}</span> {data.label}
          </span>
        </div>
        <div className="h-[3px] overflow-hidden bg-[var(--co-line)]">
          <div
            className={clsx('h-full', t.bar)}
            style={{ width: `${Math.min(100, Math.max(0, data.pct))}%` }}
          />
        </div>
      </div>
    );
  };

  if (!firstResponse && !resolution) return null;

  return (
    <div className="flex items-end gap-8">
      {firstResponse && row('First response', firstResponse)}
      {resolution && row('Resolution', resolution)}
    </div>
  );
}

export default TicketIdentityPlate;
